import { useEffect, useState } from 'react';
import { ViewModelEntity } from '@view-model/domain/view-model';
import { RTDBPath } from 'balus-common';
import type { ViewModelTagId, GroupId, ViewModelId } from 'balus-common';
import { useCurrentUserId } from '@framework/auth';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { ObjectRepository } from '@framework/repository';
import { ViewModelTagOperation } from '@view-model/domain/view-model/ViewModelTagOperation';
import { Workspace } from '@workspace/domain/workspace';

export type ViewModelAndWorkspace = {
  viewModel: ViewModelEntity;
  workspace: Workspace;
};

type UseTagAssignedViewModelsResult = {
  viewModelAndWorkspaces: ViewModelAndWorkspace[];
  loading: boolean;
};

export const useTagAssignedViewModels = (
  groupId: GroupId,
  viewModelTagId: ViewModelTagId
): UseTagAssignedViewModelsResult => {
  const [viewModelAndWorkspaces, setViewModelAndWorkspaces] = useState<ViewModelAndWorkspace[]>([]);
  const [loading, setLoading] = useState(true);

  const currentUserId = useCurrentUserId();
  if (currentUserId === null) {
    throw new Error('Not logged in');
  }

  const [assignedViewModelIds] = useSnapshot<ViewModelId[]>({
    path: RTDBPath.ViewModelTags.tagsAssignedViewModelsPath(groupId, viewModelTagId),
    load: ({ snapshot }) => (snapshot.val() !== null ? Object.keys(snapshot.val()) : []),
    onError: (error) => {
      console.error('Failed to load assigned view model IDs:', error);
      setLoading(false);
    },
  });

  useEffect(() => {
    if (assignedViewModelIds === null) return;

    async function init(userId: string, viewModelIds: string[]) {
      setLoading(true);
      try {
        const list = await Promise.all(
          viewModelIds.map(async (viewModelId) => {
            return {
              viewModelId,
              isVisible: await ViewModelTagOperation.isViewModelVisible(userId, groupId, viewModelId),
            };
          })
        );
        const visibleViewModelIds = list.filter(({ isVisible }) => isVisible).map(({ viewModelId }) => viewModelId);
        const viewModelAndWorkspaceEntities = await Promise.all(
          visibleViewModelIds.map(async (viewModelId) => {
            const viewModel = await new ObjectRepository(
              ViewModelEntity,
              RTDBPath.ViewModel.viewModelPath(viewModelId)
            ).get();
            if (viewModel === null) {
              return null;
            }
            const workspace = await new ObjectRepository(
              Workspace,
              RTDBPath.Workspace.workspaceEntityPath(viewModel.workspaceId)
            ).get();
            if (workspace === null) {
              return null;
            }

            return {
              viewModel,
              workspace,
            };
          })
        );

        setViewModelAndWorkspaces(
          viewModelAndWorkspaceEntities
            .filter((entitySet) => entitySet !== null)
            .sort((a, b) => ViewModelEntity.compare(a.viewModel, b.viewModel))
        );
      } catch (e) {
        console.error('Failed to load tag assigned view models:', e);
      } finally {
        setLoading(false);
      }
    }

    init(currentUserId, assignedViewModelIds);
  }, [assignedViewModelIds, currentUserId, groupId]);

  return { viewModelAndWorkspaces, loading };
};
